/**
 * Locale-aware date/number formatting helpers.
 *
 * All formatting routes through the active UI language (from i18next) so dates
 * and numbers match the chosen interface language instead of the browser's
 * default. Use these instead of hardcoding `'en-US'` or calling
 * `toLocaleString()` with no locale.
 */

import {
  formatDistanceToNow as dfFormatDistanceToNow,
  type FormatDistanceToNowOptions,
} from 'date-fns'
import { enUS, es, it, nl, ptBR } from 'date-fns/locale'
import type { Locale } from 'date-fns'

import i18n from '@/i18n'
import { toSupportedLocale, type LocaleCode } from '@/i18n/locales'

// Keyed by LocaleCode, not string, so adding a locale to SUPPORTED_LOCALES without
// a line here is a compile error. It used to be a Record<string, Locale> that fell
// back to English, which is how Italian shipped rendering "3 days ago" on an
// otherwise fully translated page.
const DATE_FNS_LOCALES: Record<LocaleCode, Locale> = {
  en: enUS,
  es,
  it,
  nl,
  // Brazilian Portuguese: the catalog is written in it (`você`, `equipe`), and the
  // marketing site serves this locale under a /pt-br/ prefix.
  pt: ptBR,
}

/** BCP-47 tag for Intl APIs based on the active UI language (e.g. 'es'). */
export function activeIntlLocale(): LocaleCode {
  return toSupportedLocale(i18n.resolvedLanguage)
}

/** date-fns Locale object for the active UI language. */
export function activeDateFnsLocale(): Locale {
  return DATE_FNS_LOCALES[activeIntlLocale()]
}

/** Format a date/string with Intl.DateTimeFormat in the active locale. */
export function formatDate(
  value: Date | string | number,
  options?: Intl.DateTimeFormatOptions
): string {
  const date = value instanceof Date ? value : new Date(value)
  return new Intl.DateTimeFormat(activeIntlLocale(), options).format(date)
}

/** Format a number with Intl.NumberFormat in the active locale. */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(activeIntlLocale(), options).format(value)
}

/** Relative time (e.g. "2 days ago") in the active locale. */
export function formatDistanceToNow(
  value: Date | string | number,
  options?: Omit<FormatDistanceToNowOptions, 'locale'>
): string {
  const date = value instanceof Date ? value : new Date(value)
  return dfFormatDistanceToNow(date, { ...options, locale: activeDateFnsLocale() })
}

/**
 * A local calendar day as YYYY-MM-DD.
 *
 * Not `toISOString().slice(0, 10)`: that converts to UTC first, so a date picked in a
 * timezone west of UTC comes back as the previous day. The user picked a day on a
 * calendar, and that day is what the server should filter on.
 */
export function toIsoDateString(value: Date): string {
  const month = `${value.getMonth() + 1}`.padStart(2, '0')
  const day = `${value.getDate()}`.padStart(2, '0')
  return `${value.getFullYear()}-${month}-${day}`
}

/**
 * A distance in the units the business's own market uses.
 *
 * The unit comes from the business's country, not the UI language: an Italian-speaking
 * owner of a shop in Ohio still thinks about that market in miles. The server decides
 * which it is and sends `imperial_units`.
 */
export function formatDistance(meters: number, imperial: boolean): string {
  if (imperial) {
    const miles = meters / 1609.344
    return miles < 0.1
      ? `${formatNumber(Math.round(meters * 3.28084))} ft`
      : `${formatNumber(miles, { maximumFractionDigits: 1 })} mi`
  }
  return meters < 1000
    ? `${formatNumber(meters)} m`
    : `${formatNumber(meters / 1000, { maximumFractionDigits: 1 })} km`
}
